import {
    AdvancedSearchCondition,
    AdvancedSearchConditionNodeType,
    AdvancedSearchRelativeDateInterval,
    AdvancedSearchRequest,
    AdvancedSearchRequestCondition,
    AdvancedSearchRequestRelativeDate,
    EntityRegionModeOutput,
    EntityRegionVisibilityInput,
    EntityRegionVisibilityRegion,
    FieldConditionOperator,
} from "generated/graphql";

export type ConditionEditorMode = "simple" | "advanced";

export const syncCommittedColumns = (
    editorInput: AdvancedSearchRequest,
    committed: AdvancedSearchRequest,
): AdvancedSearchRequest => {
    if (committed.entityType !== editorInput.entityType) {
        return editorInput;
    }
    return {
        ...editorInput,
        sort: committed.sort,
        select: committed.select,
        summaries: committed.summaries,
    };
};

export const simplifiableCondition = (
    condition: AdvancedSearchRequestCondition,
): boolean => {
    if (!condition) {
        return false;
    }
    if (condition.nodeType !== AdvancedSearchConditionNodeType.And) {
        return false;
    }
    if (
        !condition.children.every(c => c.nodeType === AdvancedSearchConditionNodeType.Or)
    ) {
        return false;
    }
    if (
        !condition.children.every(c =>
            c.children.every(
                cc =>
                    cc.nodeType === AdvancedSearchConditionNodeType.FieldCondition ||
                    cc.nodeType === AdvancedSearchConditionNodeType.RelationCondition,
            ),
        )
    ) {
        return false;
    }
    if (
        !condition.children.every(c =>
            c.children.every(cc => {
                if (cc.nodeType !== AdvancedSearchConditionNodeType.RelationCondition) {
                    return true;
                }
                if (cc.children.length !== 1) {
                    return false;
                }
                const child = cc.children[0];
                return simplifiableCondition(child);
            }),
        )
    ) {
        return false;
    }
    return true;
};

export const countFiltersApplied = (
    condition: AdvancedSearchCondition | null,
): number => {
    if (!condition) {
        return 0;
    }

    if (condition.nodeType === AdvancedSearchConditionNodeType.FieldCondition) {
        return 1;
    }

    let sum = 0;

    condition.children?.forEach(c => {
        sum += countFiltersApplied(c);
    });

    return sum;
};

export const getRegionVisibilityForDuplication = (
    isShared: boolean,
    regionVisibility: { regions: EntityRegionVisibilityRegion[] } | null | undefined,
): EntityRegionVisibilityInput | undefined => {
    if (!isShared || !regionVisibility?.regions) {
        return undefined;
    }

    const nonInheritedRegions = regionVisibility.regions.filter(
        r => r.mode !== EntityRegionModeOutput.Inherited,
    );

    if (nonInheritedRegions.length === 0) {
        return undefined;
    }

    return {
        regions: nonInheritedRegions.map(r => ({
            mode: r.mode,
            regionId: r.regionId,
        })),
    };
};

export const relativeDateForOperator = (
    condition: AdvancedSearchRequestCondition,
    newOperator: FieldConditionOperator,
): AdvancedSearchRequestRelativeDate | null => {
    const relativeDate = condition.relativeDate ?? null;
    if (
        relativeDate?.interval === AdvancedSearchRelativeDateInterval.Thismonth &&
        newOperator !== FieldConditionOperator.DateMonthEquals
    ) {
        return null;
    }
    return relativeDate;
};
